<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { Icon } from '@iconify/vue'
import emitter from '../utils/emitter'

function pagePos() {
  if (typeof window === 'undefined') return { left: 0, top: 8, width: 520 }
  const maxWidth = Math.max(240, window.innerWidth - 24)
  const width = Math.min(520, maxWidth)
  return {
    width,
    left: Math.max(8, Math.round((window.innerWidth - width) / 2)),
    top: 8,
  }
}

function setPos(next) {
  if (pos.left === next.left && pos.top === next.top && pos.width === next.width) return
  pos.left = next.left
  pos.top = next.top
  pos.width = next.width
}

const notice = ref(null)
const box = ref(null)
const anchored = ref(false)
const pos = reactive(pagePos())
let hideTimer = null
let resizeObserver = null
let mutationObserver = null

const titles = { success: 'Success', error: 'Error', warning: 'Warning', info: 'Info' }
const icons = {
  success: 'fa6-solid:circle-check',
  error: 'fa6-solid:circle-xmark',
  warning: 'fa6-solid:triangle-exclamation',
  info: 'fa6-solid:circle-info',
}

function findDialogContent() {
  const overlays = [...document.querySelectorAll('.v-overlay--active')]
    .filter((overlay) => {
      if (overlay.closest('.v-snackbar')) return false
      if (overlay.classList.contains('v-snackbar')) return false
      if (overlay.classList.contains('v-menu')) return false
      if (overlay.classList.contains('v-tooltip')) return false
      if (overlay.classList.contains('v-navigation-drawer')) return false
      const isDialog = overlay.classList.contains('v-dialog')
        || !!overlay.querySelector(':scope > .v-overlay__scrim')
      if (!isDialog) return false
      const content = overlay.querySelector(':scope > .v-overlay__content')
      if (!content?.getClientRects().length) return false
      const rect = content.getBoundingClientRect()
      return rect.width > 80 && rect.height > 80
    })
  const overlay = overlays.at(-1)
  return overlay?.querySelector(':scope > .v-overlay__content') || null
}

function applyPagePos() {
  anchored.value = false
  setPos(pagePos())
}

function place() {
  const el = findDialogContent()
  if (!el) {
    applyPagePos()
    return false
  }
  const rect = el.getBoundingClientRect()
  const height = box.value?.offsetHeight || 48
  const maxWidth = Math.max(240, window.innerWidth - 16)
  const width = Math.min(Math.round(rect.width), maxWidth)
  let left = Math.round(rect.left)
  if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - width)
  if (left < 8) left = 8
  const top = Math.max(8, Math.round(rect.top - height - 8))
  anchored.value = true
  setPos({ left, top, width })
  return true
}

function unwatch() {
  window.removeEventListener('resize', onLayout)
  window.removeEventListener('scroll', onLayout, true)
  resizeObserver?.disconnect()
  resizeObserver = null
  mutationObserver?.disconnect()
  mutationObserver = null
}

function onLayout() {
  if (!notice.value) return
  try {
    place()
  } catch {}
}

function isNoticeMutation(record) {
  const node = record.target?.nodeType === 1 ? record.target : record.target?.parentElement
  return !!node?.closest?.('.dialog-notice-host')
}

function watchDialog() {
  unwatch()
  window.addEventListener('resize', onLayout)
  window.addEventListener('scroll', onLayout, true)
  const el = findDialogContent()
  if (el && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(onLayout)
    resizeObserver.observe(el)
  }
  mutationObserver = new MutationObserver((records) => {
    if (records.every(isNoticeMutation)) return
    onLayout()
  })
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  })
}

function hide() {
  if (hideTimer) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
  notice.value = null
  unwatch()
}

function show(payload) {
  const next = typeof payload === 'string' ? { message: payload } : payload || {}
  const type = next.type || 'success'
  notice.value = {
    title: next.title || titles[type] || 'Info',
    message: next.message || '',
    type,
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        place()
      } catch {
        applyPagePos()
      }
      watchDialog()
    })
  })
  if (hideTimer) window.clearTimeout(hideTimer)
  if (next.persistent) return
  hideTimer = window.setTimeout(hide, next.timeout ?? 2800)
}

function bind() {
  emitter.off('dialog-notify', show)
  emitter.on('dialog-notify', show)
}

bind()
onMounted(bind)
onBeforeUnmount(() => {
  emitter.off('dialog-notify', show)
  hide()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog-notice">
      <div
        v-if="notice"
        ref="box"
        class="nr-notify dialog-notice-host"
        :class="['nr-notify--' + notice.type, { 'dialog-notice-host--page': !anchored }]"
        role="status"
        :style="{ left: pos.left + 'px', top: pos.top + 'px', width: pos.width + 'px' }"
      >
        <div class="nr-notify__row">
          <span class="nr-notify__icon" aria-hidden="true">
            <Icon :icon="icons[notice.type] || icons.info" width="15" height="15" />
          </span>
          <div class="nr-notify__text">
            <div class="nr-notify__title">{{ notice.title }}</div>
            <div class="nr-notify__message">{{ notice.message }}</div>
          </div>
          <button type="button" class="nr-notify__close" aria-label="Dismiss" @click="hide">
            <Icon icon="fa6-solid:xmark" width="12" height="12" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
